'use client';

import { type KeyboardEvent, useRef, useState } from 'react';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { useTranslations } from 'next-intl';
import { readableColor } from 'polished';

import type { OrganizationDetailsQuery } from '@/common/__generated__/graphql';
import { getActionTermContext } from '@/common/i18n';
import { OrganizationLink } from '@/common/links';
import RichText from '@/components/common/RichText';
import { Col, Container, Row } from '@/components/common/layout/LayoutGrid';
import ActionStatusTable from '@/components/dashboard/ActionStatusTable';
import type { ColumnConfig } from '@/components/dashboard/dashboard.types';
import PlanChip from '@/components/plans/PlanChip';
import { usePlan } from '@/context/plan';

const Tab = styled.button`
  background: ${(props) => props.theme.orgPageBackgroundColor};
  color: ${(props) =>
    readableColor(
      props.theme.orgPageBackgroundColor,
      props.theme.themeColors.black,
      props.theme.themeColors.white
    )};
  padding: ${(props) => props.theme.spaces.s100};
  border-radius: ${(props) => props.theme.btnBorderRadius} ${(props) => props.theme.btnBorderRadius}
    0 0;
  display: inline-block;
  border: none;
  margin: 0;
  text-decoration: none;
  cursor: pointer;
  text-align: left;

  &:hover,
  &:focus {
    color: ${(props) => props.theme.brandLight};
  }
  &.active {
    color: ${(props) => props.theme.orgPageBackgroundColor};
    background: ${(props) => props.theme.themeColors.white};
    &:hover {
      color: ${(props) => props.theme.themeColors.black};
    }
  }
`;

/** The only plan, shown like the active tab but not interactive. */
const SinglePlan = styled(Tab.withComponent('div'))`
  &,
  &:hover {
    cursor: default;
    color: ${(props) => props.theme.orgPageBackgroundColor};
    background: ${(props) => props.theme.themeColors.white};
  }
`;

const OrgTabs = styled.div`
  background-color: ${(props) => props.theme.orgPageBackgroundColor};
  margin-bottom: 0;
`;

const TabList = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow: auto;
`;

const HeaderContainer = styled(Container)`
  margin-bottom: ${(props) => props.theme.spaces.s200};
`;

const OrgHeader = styled.div`
  padding: ${(props) => props.theme.spaces.s300} 0 0 0;
  background-color: ${(props) => props.theme.orgPageBackgroundColor};
  color: ${(props) =>
    readableColor(
      props.theme.orgPageBackgroundColor,
      props.theme.themeColors.black,
      props.theme.themeColors.white
    )};

  h1 {
    margin-bottom: ${(props) => props.theme.spaces.s150};
    color: inherit;
    line-height: ${(props) => props.theme.lineHeightSm};
    font-size: ${(props) => props.theme.fontSizeXl};
  }

  a {
    color: inherit;
    line-height: ${(props) => props.theme.lineHeightSm};

    &:hover {
      color: inherit;
    }
  }

  ${(props) => props.theme.breakpoints.up('md')} {
    h1 {
      font-size: ${(props) => props.theme.fontSizeXxl};
    }
  }
`;

const SectionTitle = styled.p`
  font-weight: ${(props) => props.theme.fontWeightBold};
`;

const OrgLogo = styled.img`
  display: block;
  background-color: aliceblue;
  margin-bottom: ${(props) => props.theme.spaces.s200};
  max-width: ${(props) => props.theme.spaces.s800};

  ${(props) => props.theme.breakpoints.up('md')} {
    width: 100%;
    max-width: 100%;
  }
`;

const OrgDescription = styled.div`
  font-size: ${(props) => props.theme.fontSizeSm};
`;

/** The URL without its protocol and trailing slash */
const formatUrl = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

const OrgContact = styled.p`
  font-size: ${(props) => props.theme.fontSizeSm};
  margin-bottom: 0;
`;

const OrgLink = styled.a`
  display: block;
  font-size: ${(props) => props.theme.fontSizeSm};
  text-decoration: underline;
  color: ${(props) => props.theme.brandLight};
`;

const ActionTableHeader = styled.div`
  margin: ${(props) => props.theme.spaces.s300} 0 ${(props) => props.theme.spaces.s100};

  h2 {
    font-size: ${(props) => props.theme.fontSizeMd};
    margin: 0;
  }
`;

const ActionTableContainer = styled.div`
  overflow: auto;
`;

const PANEL_ID = 'org-plan-panel';
const tabId = (index: number) => `org-plan-tab-${index}`;

type Props = {
  org: NonNullable<OrganizationDetailsQuery['organization']>;
  planFromOrgQuery: NonNullable<OrganizationDetailsQuery['plan']>;
  testId?: string;
};

function OrgContent({ org, planFromOrgQuery, testId }: Props) {
  const plan = usePlan();
  const t = useTranslations();
  const theme = useTheme();
  // Light text on the tabs only when their background is dark
  const darkTabBackground =
    readableColor(
      theme.orgPageBackgroundColor,
      theme.themeColors.black,
      theme.themeColors.white,
      false
    ) === theme.themeColors.white;
  const [selectedPlanIndex, setSelectedPlan] = useState(0);

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Make sure host plan is first
  const allPlans = [planFromOrgQuery, ...org.plansWithActionResponsibilities];
  const selectedPlan = allPlans[selectedPlanIndex];

  const handleTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const last = allPlans.length - 1;
    const index = {
      ArrowLeft: selectedPlanIndex === 0 ? last : selectedPlanIndex - 1,
      ArrowRight: selectedPlanIndex === last ? 0 : selectedPlanIndex + 1,
      Home: 0,
      End: last,
    }[e.key];
    if (index === undefined) return;
    e.preventDefault();
    setSelectedPlan(index);
    tabRefs.current[index]?.focus();
  };

  const renderPlanChip = (p: (typeof allPlans)[number], selected: boolean) => (
    <PlanChip
      planImage={p.image?.rendition?.src}
      planShortName={`${p.shortName || p.name}${p.versionName ? ` (${p.versionName})` : ''}`}
      organization={
        p.shortName ? p.name : p.organization?.abbreviation || p.organization?.name || ''
      }
      size="md"
      negative={!selected && darkTabBackground}
    />
  );

  return (
    <div className="mb-5" data-testid={testId}>
      <OrgHeader>
        <HeaderContainer>
          <Row>
            <Col md={{ size: 6, offset: org.logo?.rendition?.src ? 2 : 0 }}>
              <SectionTitle>
                {t('organizations', {
                  context: plan.generalContent.organizationTerm,
                })}
              </SectionTitle>
            </Col>
          </Row>
          <Row>
            {org.logo?.rendition?.src && (
              <Col md="2">
                {/* The name is in the heading beside it */}
                <OrgLogo src={org.logo?.rendition?.src} alt="" />
              </Col>
            )}
            <Col md="8" xl="7" className="mb-5">
              {org.parent?.id && (
                <>
                  <OrganizationLink organizationId={org.parent.id}>
                    {org.parent.name}
                  </OrganizationLink>{' '}
                  /
                </>
              )}
              <h1>{org.name}</h1>
              {org.description && (
                <OrgDescription>
                  <RichText html={org.description} />
                </OrgDescription>
              )}
              {org.email && (
                <OrgContact>
                  {t('email')}: <a href={`mailto:${org.email}`}>{org.email}</a>
                </OrgContact>
              )}
              {org.url && <OrgLink href={org.url}>{formatUrl(org.url)}</OrgLink>}
            </Col>
          </Row>
        </HeaderContainer>
        <OrgTabs>
          <Container>
            {allPlans.length === 1 ? (
              <SinglePlan>{renderPlanChip(allPlans[0], true)}</SinglePlan>
            ) : (
              <TabList role="tablist" aria-label={t('plan')}>
                {allPlans.map((p, i) => (
                  <Tab
                    key={p.id}
                    ref={(el) => {
                      tabRefs.current[i] = el;
                    }}
                    type="button"
                    className={i === selectedPlanIndex ? 'active' : ''}
                    role="tab"
                    id={tabId(i)}
                    aria-selected={i === selectedPlanIndex}
                    aria-controls={PANEL_ID}
                    // Only the selected tab is in the tab order; arrow keys move between tabs
                    tabIndex={i === selectedPlanIndex ? 0 : -1}
                    onClick={() => setSelectedPlan(i)}
                    onKeyDown={handleTabKeyDown}
                  >
                    {renderPlanChip(p, i === selectedPlanIndex)}
                  </Tab>
                ))}
              </TabList>
            )}
          </Container>
        </OrgTabs>
      </OrgHeader>
      {allPlans.length ? (
        <Container
          fluid="lg"
          data-testid="org-actions-container"
          {...(allPlans.length > 1
            ? { role: 'tabpanel', id: PANEL_ID, 'aria-labelledby': tabId(selectedPlanIndex) }
            : {})}
        >
          <ActionTableHeader>
            <h2>
              {t('org-responsible-in-actions', {
                actionCount: selectedPlan.actions.length,
                ...getActionTermContext(plan),
              })}
            </h2>
          </ActionTableHeader>
          <ActionTableContainer>
            <ActionStatusTable
              columns={
                planFromOrgQuery.actionListPage?.__typename === 'ActionListPage'
                  ? ((planFromOrgQuery.actionListPage.dashboardColumns ?? []) as ColumnConfig[])
                  : []
              }
              enableExport={false}
              planViewUrl={selectedPlan.viewUrl}
              plan={selectedPlan as unknown as Parameters<typeof ActionStatusTable>[0]['plan']}
              actions={
                selectedPlan.actions as unknown as Parameters<
                  typeof ActionStatusTable
                >[0]['actions']
              }
              orgs={[]}
            />
          </ActionTableContainer>
        </Container>
      ) : (
        <Container />
      )}
    </div>
  );
}

export default OrgContent;
